// Packages
import React, { useState, useEffect, useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";

// MUI
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Divider,
  Typography,
  Grid,
  Chip
} from "@mui/material";

// Icon
import { ReactComponent as CheckedIcon } from "assets/images/icons/icn_checked.svg";
import { ReactComponent as UnCheckedIconIcon } from "assets/images/icons/icn_unchecked.svg";

// Components
import GlobalAlert from "components/Notification";
import { SolidButton } from "components/buttons/SolidButton";
import PillTab from 'components/PillTab';
import { Loading } from 'components/Loading';
import { OutlineButton } from 'components/buttons/OutlineButton';

// Custom UI
import { StyledRadioButtonGroup } from "./components/StyledRadioButtonGroup";
import { StyledRadioMenuSelect } from './components/StyledRadioMenuSelect';
import { StyledTimeChipSelector } from './components/StyledTimeChipSelector';
import { StyledInput } from "./components/StyledInput";
import { StyledBox } from "./components/StyledBox";

// Hook
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

// Helper
import {
  getMainSendRequestParams,
  getInitData,
  getInitStartTimeList,
  getCleanPayload,
  getIsFormInvalid,
} from "./helper";

// Const
import {
  defaultAlertInfo,
  defaultBandTabs,
  defaultWirelessModes,
  defaultChannelWidths,
  defaultUtilizationList,
  defaultRSSIThresholdList,
} from "./const";

const POCMode = true;

const Radio = ({
  showEditGeneral
}) => {
  const [uuids] = useState(() => ({
    read: uuidv4(),
    update: uuidv4(),
  }));
  const theme = useTheme();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const { auth, isAuthReady } = useAuthScope();

  const [alertInfo, setAlertInfo] = useState(structuredClone(defaultAlertInfo));
  const [isLoading, setIsLoading] = useState(false);

  const [radioData, setRadioData] = useState(null);
  const [radioOriginalData, setRadioOriginalData] = useState(null);
  const [bandTabs, setBandTabs] = useState(structuredClone(defaultBandTabs));
  const startTimeList = useMemo(() => getInitStartTimeList({ radioData }), [radioData]);

  const selectedBand = useMemo(() => bandTabs.find(tab => tab.isSelected), [bandTabs]);
  const [channelWidths, setChannelWidths] = useState(structuredClone(defaultChannelWidths));
  const [wirelessModes, setWirelessModes] = useState(structuredClone(defaultWirelessModes));

  const fetchRadioData = () => {
    setIsLoading(true);

    if (isAuthReady) {
      const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: "read" });

      sendRequest(
        apiUrl,
        uuids.read,
        requestParams
      );
    }
  }

  const onUpdateRadioData = ({
    key,
    value
  }) => {

    setRadioData(prev => {
      return {
        ...prev,
        [key]: value
      }
    })

  }

  const onUpdateGlobalRadioData = ({
    key,
    value
  }) => {

    setRadioData(prev => {
      return {
        ...prev,
        "wireless.radioGlobal": {
          ...prev["wireless.radioGlobal"],
          [key]: value,
        }
      }
    })

  }

  const onAdd = time => {
    onUpdateGlobalRadioData({
      key: "neighborApDetectionSchedule",
      value: [...radioData["wireless.radioGlobal"].neighborApDetectionSchedule, time]
    });
  };

  const onDelete = time => {
    onUpdateGlobalRadioData({
      key: "neighborApDetectionSchedule",
      value: radioData["wireless.radioGlobal"].neighborApDetectionSchedule.filter(loopTime => loopTime != time)
    });
  };

  const onSave = () => {
    setIsLoading(true);

    const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: "update" });
    const payload = getCleanPayload({ radioData });

    sendRequest(
      apiUrl,
      uuids.update,
      requestParams,
      payload
    );
  }

  const onCancel = () => {
    setRadioData(structuredClone(radioOriginalData));
  }

  useEffect(() => {
    fetchRadioData();
  }, [isAuthReady]);

  useEffect(() => {
    if (!dataMap?.[uuids.read]) return;

    const response = getData(uuids.read);
    const responseData = response?.data ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to get the Radio data.",
        });
      } else {

        const initData = getInitData({
          responseData
        });

        setRadioData(initData);
        setRadioOriginalData(initData);
      }

      setIsLoading(false);
    }
  }, [dataMap?.[uuids.read]]);

  useEffect(() => {
    if (!dataMap?.[uuids.update]) return;

    const response = getData(uuids.update);
    const responseData = response?.data ?? response ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to update the Radio data.",
        });

        fetchRadioData();

      }

      setIsLoading(false);
    }
  }, [dataMap?.[uuids.update]]);

  if (POCMode) {
    return (
      <>

        {(isLoading || !radioData) && <Loading />}

        {/* Global Settings */}
        <Box sx={{ padding: "10px 20px" }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[1],
            }}
          >
            Global Settings
          </Typography>

          <Divider sx={{ margin: "20px 0" }} />

          <Box sx={{ display: "flex" }}>
            <StyledRadioButtonGroup
              {...{
                showEditGeneral,
                label: "Neighbor AP Detection",
                value: radioData?.["wireless.radioGlobal"]?.neighborApDetection || "",
                onChange: value => {
                  onUpdateGlobalRadioData({
                    key: "neighborApDetection",
                    value
                  })
                }
              }}
            />
            <Chip
              label="PRE-VIEW"
              size="small"
              sx={{
                backgroundColor: "#FFBC31",
                color: "#fff",
                fontWeight: 700,
                fontSize: 12,
                borderRadius: 5,
                height: 24,
                px: 0.7,
                mb: 2,
                mt: 2.9,
                "& .MuiChip-label": {
                  lineHeight: 1,
                  paddingTop: "1px",
                },
              }}
            />
          </Box>

          <StyledTimeChipSelector
            label="Start Time (Each day)"
            timeList={startTimeList}
            max={9}
            onAddTimeChip={onAdd}
            onDeleteTimeChip={onDelete}
          />

        </Box>

        {
          showEditGeneral && (
            <Box>
              <Grid
                container
                style={{ justifyContent: "center", width: "100%", marginTop: "65px" }}
              >
                <Box style={{ margin: "10px" }}>
                  <OutlineButton
                    width="135px"
                    height="42px"
                    onClick={() => onCancel()}
                    disabled={showEditGeneral ? false : true}
                  >
                    Cancel
                  </OutlineButton>
                </Box>

                <Box style={{ margin: "10px" }}>
                  <SolidButton
                    width="135px"
                    height="42px"
                    onClick={() => onSave()}
                    disabled={getIsFormInvalid({ radioData })}
                  >
                    Save
                  </SolidButton>
                </Box>
              </Grid>
            </Box>
          )
        }

        <GlobalAlert
          open={alertInfo.open}
          severity={alertInfo.severity}
          message={alertInfo.message}
          onClose={() => setAlertInfo(structuredClone(defaultAlertInfo))}
        />
      </>
    );
  }

  return (
    <>
      {/* Global Settings */}
      <Box sx={{ padding: "10px 20px" }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[1],
          }}
        >
          Global Settings
        </Typography>

        <Divider sx={{ margin: "20px 0" }} />

        <StyledRadioButtonGroup
          {...{
            showEditGeneral,
            label: "Airtime Fairness",
            value: "",
            onChange: () => { }
          }}
        />
        <StyledRadioButtonGroup
          {...{
            showEditGeneral,
            label: "Band Steering",
            value: "",
            onChange: () => { }
          }}
        />

        <StyledRadioButtonGroup
          {...{
            showEditGeneral,
            label: "Neighbor AP Detection",
            value: "",
            onChange: () => { }
          }}
        />

        <StyledTimeChipSelector
          label="Start Time (Each day)"
          timeList={startTimeList}
          max={9}
          onAddTimeChip={onAdd}
          onDeleteTimeChip={onDelete}
        />

        <StyledRadioButtonGroup
          {...{
            showEditGeneral,
            label: "Auto Failover",
            value: "",
            onChange: () => { }
          }}
        />

        <StyledRadioButtonGroup
          {...{
            showEditGeneral,
            label: "Smart Wi-Fi Disconnect",
            value: "",
            onChange: () => { }
          }}
        />

      </Box>

      {/* WLAN Optimization */}
      <Box sx={{ padding: "10px 20px" }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[1],
          }}
        >
          WLAN Optimization
        </Typography>

        <Divider sx={{ margin: "20px 0" }} />

        <StyledRadioButtonGroup
          {...{
            showEditGeneral,
            label: "Auto Channel Adjustment",
            value: "",
            onChange: () => { }
          }}
        />

        <StyledRadioButtonGroup
          {...{
            showEditGeneral,
            label: "- Auto Power Adjustment",
            value: "",
            isIndent: true,
            onChange: () => { }
          }}
        />

        <StyledTimeChipSelector
          label="- Start Time (Each day)"
          timeList={startTimeList}
          max={9}
          isIndent
          onAddTimeChip={() => { }}
          onDeleteTimeChip={() => { }}
        />

      </Box>

      <Divider sx={{ margin: "20px 0" }} />

      {/* Each Radio settings */}
      <Box
        sx={{
          ml: 2
        }}
      >
        <Box sx={{
          my: 4
        }}>
          <PillTab
            options={bandTabs}
            value={selectedBand?.value || ""}
            onChange={band => {
              setBandTabs(prev => {
                return prev.map(loopBand => {
                  return {
                    ...loopBand,
                    isSelected: loopBand.value === band.value
                  }
                })
              })
            }}
          />
        </Box>

        <StyledRadioMenuSelect
          {...{
            width: "325px",
            showEditGeneral,
            options: selectedBand?.value ? channelWidths[selectedBand?.value] : [],
            label: "Channel Width",
            value: channelWidths[selectedBand?.value]?.find(ch => ch.isSelected)?.value || "",
            onChange: () => { }
          }}
        />

        {/* NEED TO DO : Rest UI */}

      </Box>

      {
        showEditGeneral && (
          <Box>
            <Grid
              container
              style={{ justifyContent: "center", width: "100%" }}
            >
              <Box style={{ margin: "10px" }}>
                <OutlineButton
                  width="135px"
                  height="42px"
                  onClick={() => onCancel()}
                  disabled={showEditGeneral ? false : true}
                >
                  Cancel
                </OutlineButton>
              </Box>

              <Box style={{ margin: "10px" }}>
                <SolidButton
                  width="135px"
                  height="42px"
                  onClick={() => onSave()}
                  disabled={getIsFormInvalid({ radioData })}
                >
                  Save
                </SolidButton>
              </Box>
            </Grid>
          </Box>
        )
      }

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo(structuredClone(defaultAlertInfo))}
      />
    </>
  );
};

export default Radio;
